import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  PreventiveMaintenanceService,
  type CreateTaskBody,
  type UpdateTaskBody,
} from '~/app/services/preventive-maintenance/preventive-maintenance.service';

export function useTasks() {
  const { user } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationId = user?.organizationId ?? '';
  const scopeKey = isSuperAdmin ? 'all-orgs' : organizationId;

  return useQuery({
    queryKey: ['preventive-maintenance', 'tasks', scopeKey],
    queryFn: async () => {
      const res = await PreventiveMaintenanceService.listTasks();
      return res.data;
    },
    enabled: isSuperAdmin || !!organizationId,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTaskBody) =>
      PreventiveMaintenanceService.createTask(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'tasks'],
      });
    },
  });
}

export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTaskBody }) =>
      PreventiveMaintenanceService.updateTask(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['preventive-maintenance', 'tasks'],
      });
    },
  });
}
